/**
 * The typed client for our own API.
 *
 * Every path and every request/response shape comes from `ROUTES` and `RouteContract`
 * in the engine package, resolved by `call()` below. That is deliberate: the contract
 * exists so a client cannot send or expect the wrong shape and still compile (#2), and a
 * hand-written `fetch("/api/teams")` here would throw that away — the path would be
 * written twice and the two copies would drift.
 *
 * The frontend holds no credential of any kind. The session is an httpOnly cookie the
 * browser cannot read, which is why every request sends `credentials: "include"` and
 * none of them attach a token (ADR-0008). It follows that the *only* way to know whether
 * anyone is signed in is to ask the server: see `me()`.
 *
 * Paths are same-origin and relative, with no configurable base. One process serves both
 * the API and this bundle (ADR-0012), and dev is proxied to match (`vite.config.ts`) — so
 * a base url would be a second way to describe an origin the browser already knows, and
 * getting it wrong breaks credentialed requests in dev only.
 */
import { ROUTES } from "@sportsos/engine";
import type { CachedRequest, ReadCache } from "./cache/readCache.ts";
import type {
  AddTournamentTeamsRequest,
  ApiError,
  AssignScorerRequest,
  CompleteMatchResponse,
  CreateMatchRequest,
  CreatePlayerRequest,
  CreateRuleSetRequest,
  CreateTeamInviteRequest,
  CreateTeamRequest,
  CreateTournamentRequest,
  Fixture,
  GenerateFixturesRequest,
  GenerateFixturesResponse,
  GetEventsResponse,
  InvitePreviewResponse,
  JoinTeamResponse,
  LeaderboardResponse,
  LiveMatchesResponse,
  Match,
  MatchesResponse,
  MeResponse,
  MergePlayersRequest,
  PlayerStyle,
  UpdatePlayerStyleRequest,
  OkResponse,
  Player,
  PlayerId,
  PlayerStatsResponse,
  PlayersResponse,
  PushKeyResponse,
  PushSubscribeRequest,
  RouteName,
  RouteRequest,
  RouteResponse,
  RuleSet,
  RuleSetsResponse,
  SendOtpRequest,
  SessionResponse,
  SignInRequest,
  SignUpRequest,
  UpdateProfileRequest,
  VerifyOtpRequest,
  StandingsResponse,
  Team,
  TeamInviteResponse,
  TeamsResponse,
  Tournament,
  TournamentDetailResponse,
  TournamentsResponse,
  UpdateFixtureRequest,
  UpdateSquadRequest,
} from "@sportsos/engine";

/**
 * A failure the API described. `body.code` is what callers switch on.
 *
 * Fields are declared and assigned rather than written as constructor parameter
 * properties, which read better and do not work: the test runner is
 * `node --experimental-strip-types`, and strip-only mode rejects parameter properties
 * outright — so any file reachable from a test cannot use them. This one is reachable.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: ApiError;

  constructor(status: number, body: ApiError) {
    super(body.message);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body;
  }
}

/** `"POST /api/teams"` → `["POST", "/api/teams"]`. */
function split(route: RouteName): readonly [string, string] {
  const [method, path] = ROUTES[route].split(" ");
  if (method === undefined || path === undefined) {
    throw new Error(`route ${route} is not "<METHOD> <path>"`);
  }
  return [method, path];
}

interface Options<N extends RouteName> {
  /** `:id`-style path parameters, substituted into the route's path. */
  readonly params?: Readonly<Record<string, string>>;
  readonly query?: Readonly<Record<string, string>>;
  readonly body?: RouteRequest<N>;
  /** Skip the read cache. For the one read whose answer must be the server's own: `me()`. */
  readonly live?: boolean;
}

/**
 * Issue one request against a named route.
 *
 * Generic over `RouteName` so the body type is the one that route declares. A wrong
 * shape is a compile error here rather than a 422 discovered on a ground.
 */
async function network<N extends RouteName>(
  route: N,
  options: Options<N> = {},
): Promise<RouteResponse<N>> {
  const [method, template] = split(route);
  const path = Object.entries(options.params ?? {}).reduce(
    (url, [key, value]) => url.replace(`:${key}`, encodeURIComponent(value)),
    template,
  );
  const query = new URLSearchParams(options.query ?? {}).toString();

  const response = await fetch(query === "" ? path : `${path}?${query}`, {
    method,
    credentials: "include",
    // Always JSON, even on a bodyless POST like sign-out: the API rejects a
    // state-changing request whose content type is not JSON, because that is what no
    // HTML form can send and is therefore what closes the login-CSRF hole (ADR-0008).
    headers: { "content-type": "application/json" },
    // Spread rather than `body: undefined`, which `exactOptionalPropertyTypes` treats as
    // a present-but-undefined property and rejects.
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  if (!response.ok) {
    // A failure body should be `ApiError`, but a proxy or a crash can answer with HTML,
    // and `.json()` would then throw something with no `code` for callers to switch on.
    const body = (await response.json().catch(() => ({
      code: "internal" as const,
      message: response.statusText || "the server could not be reached",
      fields: null,
    }))) as ApiError;
    throw new ApiRequestError(response.status, body);
  }

  // Every route returns a body — a mutation with nothing to say answers `{ ok: true }`
  // rather than 204 (see `OkResponse`), so there is no empty-body case to handle.
  return (await response.json()) as RouteResponse<N>;
}

// ── The read cache ──────────────────────────────────────────────────────────

let readCache: ReadCache | null = null;

/**
 * Turn the read cache on. Absent, every read goes to the network exactly as before — which is
 * what tests and any caller that never installs one get.
 */
export function installReadCache(cache: ReadCache | null): void {
  readCache = cache;
}

/**
 * The reads worth showing when there is no signal.
 *
 * An allowlist, like the service worker's, and for the same reason: a route added next year is
 * *not* cached until somebody decides it should be, because a cached response is a copy of
 * somebody's data left on the phone. Everything here is a GET a scorer looks at; nothing here
 * is a mutation, a search, or a per-request secret.
 */
const CACHEABLE: ReadonlySet<RouteName> = new Set<RouteName>([
  "me",
  "listMatches",
  "getMatch",
  "getEvents",
  "liveMatches",
  "recentMatches",
  "listTeams",
  "listTournaments",
  "getTournament",
  "getStandings",
  "getLeaderboard",
  "getPlayerStats",
  "listRuleSets",
]);

/** A request as the cache stores it: plain strings, so it can be asked again later. */
const asCachedRequest = <N extends RouteName>(route: N, options: Options<N>): CachedRequest => ({
  route,
  ...(options.params === undefined ? {} : { params: options.params }),
  ...(options.query === undefined ? {} : { query: options.query }),
});

async function call<N extends RouteName>(
  route: N,
  options: Options<N> = {},
): Promise<RouteResponse<N>> {
  if (readCache === null || options.live === true || !CACHEABLE.has(route)) {
    return network(route, options);
  }
  return readCache.read(asCachedRequest(route, options), () => network(route, options));
}

/** Ask the network again for a saved request, for the background refresh. Never the cache. */
export function refetchForCache(request: CachedRequest): Promise<unknown> {
  const route = request.route as RouteName;
  return network(route, {
    ...(request.params === undefined ? {} : { params: request.params }),
    ...(request.query === undefined ? {} : { query: request.query }),
  });
}

export const api = {
  // ── Auth (#4) ─────────────────────────────────────────────────────────────

  signUp: (body: SignUpRequest): Promise<SessionResponse> => call("signUp", { body }),
  signIn: (body: SignInRequest): Promise<SessionResponse> => call("signIn", { body }),
  signOut: (): Promise<OkResponse> => call("signOut"),
  sendOtp: (body: SendOtpRequest): Promise<OkResponse> => call("sendOtp", { body }),
  verifyOtp: (body: VerifyOtpRequest): Promise<SessionResponse> => call("verifyOtp", { body }),
  updateProfile: (body: UpdateProfileRequest): Promise<MeResponse> => call("updateProfile", { body }),

  /**
   * Who is signed in, or `null`.
   *
   * A 401 is the *expected* answer for a signed-out visitor, not an error, so it is
   * turned into `null` here — leaving callers to try/catch on every boot would make
   * "nobody is signed in" look like a failure at the one moment the app has nothing
   * else to go on. Any other status still throws.
   */
  /**
   * The whole response, not just `user` — the playing identity rides on it.
   *
   * This used to return `.user` alone, which is how the profile screen ended up reading
   * stats with `user.id`: with no player id in reach, a user id was the only id there was.
   */
  me: async (): Promise<MeResponse | null> => {
    try {
      // Always the server's own answer. Boot decides who is signed in from this, and a saved
      // copy answering it would make a signed-out phone look signed in.
      return await call("me", { live: true });
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.status === 401) return null;
      throw error;
    }
  },

  /**
   * The same read for a screen that only *shows* the account, so it can render with no signal.
   * Never for deciding whether anyone is signed in — that is `me()`.
   */
  meForDisplay: async (): Promise<MeResponse | null> => {
    try {
      return await call("me");
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.status === 401) return null;
      throw error;
    }
  },

  /**
   * Where to send the browser to sign in with Google.
   *
   * A url rather than a request, because this has to be a **top-level navigation**: the
   * server answers 302 to the auth vendor and sets the short-lived PKCE cookie, and a
   * `fetch` would follow that redirect inside the page and land the vendor's HTML in a
   * response body instead of in the address bar.
   */
  googleSignInUrl: (): string => split("googleStart")[1],

  // ── Formats, players, teams (#5) ──────────────────────────────────────────

  createRuleSet: (body: CreateRuleSetRequest): Promise<RuleSet> => call("createRuleSet", { body }),
  listRuleSets: (): Promise<RuleSetsResponse> => call("listRuleSets"),
  createPlayer: (body: CreatePlayerRequest): Promise<Player> => call("createPlayer", { body }),
  searchPlayers: (q: string, scope: "own" | "global" = "own"): Promise<PlayersResponse> =>
    call("searchPlayers", { query: { q, scope } }),

  /**
   * Exchange a search result's id for a real player. Call this before using a result for
   * anything — a global search also returns registered accounts that have never been
   * scored, and those carry a user id that no squad can reference.
   */
  resolvePlayer: (id: string): Promise<Player> => call("resolvePlayer", { body: { id } }),
  createTeam: (body: CreateTeamRequest): Promise<Team> => call("createTeam", { body }),
  listTeams: (): Promise<TeamsResponse> => call("listTeams"),

  /** Add players to a saved squad. Additive — there is no way to remove one in P0. */
  addTeamPlayers: (teamId: string, playerIds: readonly PlayerId[]): Promise<Team> =>
    call("addTeamPlayers", { params: { id: teamId }, body: { playerIds } }),

  // ── Team invites ──────────────────────────────────────────────────────────

  createTeamInvite: (teamId: string, body?: CreateTeamInviteRequest): Promise<TeamInviteResponse> =>
    call("createTeamInvite", { params: { id: teamId }, body: body ?? {} }),
  getTeamInvite: (teamId: string): Promise<TeamInviteResponse> =>
    call("getTeamInvite", { params: { id: teamId } }),
  revokeTeamInvite: (teamId: string): Promise<OkResponse> =>
    call("revokeTeamInvite", { params: { id: teamId } }),
  previewInvite: (code: string): Promise<InvitePreviewResponse> =>
    call("previewInvite", { params: { code } }),
  joinTeam: (code: string): Promise<JoinTeamResponse> =>
    call("joinTeam", { params: { code } }),

  // ── Matches (#5) ──────────────────────────────────────────────────────────

  createMatch: (body: CreateMatchRequest): Promise<Match> => call("createMatch", { body }),
  listMatches: (): Promise<MatchesResponse> => call("listMatches"),

  getMatch: (id: string): Promise<Match> => call("getMatch", { params: { id } }),

  updateSquad: (id: string, body: UpdateSquadRequest): Promise<Match> =>
    call("updateSquad", { params: { id }, body }),
  /** Show or hide one of your matches in other people's Live now / Recent lists. */
  setMatchVisibility: (id: string, isPublic: boolean): Promise<Match> =>
    call("setMatchVisibility", { params: { id }, body: { public: isPublic } }),

  completeMatch: (id: string): Promise<CompleteMatchResponse> =>
    call("completeMatch", { params: { id } }),

  /**
   * The whole ledger for one match, for the in-app detail view (#34).
   *
   * A summary would be cheaper and is what the *list* uses, but a scorecard is not a
   * summary: batting and bowling cards, partnerships and fall of wickets are all outputs
   * of the fold, and ADR-0002 is explicit that a stored summary may never be an input to
   * a calculation. So the detail screen reads the events and replays them — one match at
   * a time, which is the case ADR-0002's arithmetic was never about.
   */
  getEvents: (id: string): Promise<GetEventsResponse> => call("getEvents", { params: { id } }),

  // ── Live discovery (P1) ────────────────────────────────────────────────

  /**
   * Public matches being scored right now, for the home screen's live section (#43).
   *
   * Unfiltered by owner — this is the discovery surface, so it is other people's matches as
   * much as the scorer's own. Unauthenticated, so a guest sees it too.
   */
  liveMatches: (): Promise<LiveMatchesResponse> => call("liveMatches"),
  /** Everyone's most recently completed public matches, for the home screen's recent rail. */
  recentMatches: (): Promise<LiveMatchesResponse> => call("recentMatches"),

  // ── Tournaments (P1) ──────────────────────────────────────────────────
  createTournament: (body: CreateTournamentRequest): Promise<Tournament> =>
    call("createTournament", { body }),
  listTournaments: (): Promise<TournamentsResponse> => call("listTournaments"),
  getTournament: (id: string): Promise<TournamentDetailResponse> =>
    call("getTournament", { params: { id } }),
  generateFixtures: (id: string, body: GenerateFixturesRequest): Promise<GenerateFixturesResponse> =>
    call("generateFixtures", { params: { id }, body }),
  getStandings: (id: string): Promise<StandingsResponse> =>
    call("getStandings", { params: { id } }),
  getLeaderboard: (id: string): Promise<LeaderboardResponse> =>
    call("getLeaderboard", { params: { id } }),
  addTournamentTeams: (id: string, body: AddTournamentTeamsRequest): Promise<OkResponse> =>
    call("addTournamentTeams", { params: { id }, body }),
  updateFixture: (id: string, matchId: string, body: UpdateFixtureRequest): Promise<Fixture> =>
    call("updateFixture", { params: { id, matchId }, body }),
  assignScorer: (id: string, body: AssignScorerRequest): Promise<OkResponse> =>
    call("assignScorer", { params: { id }, body }),

  // ── Player stats and identity (P1) ────────────────────────────────────
  getPlayerStats: (id: string, format?: string): Promise<PlayerStatsResponse> =>
    call("getPlayerStats", { params: { id }, ...(format ? { query: { format } } : {}) }),
  claimPlayer: (id: string): Promise<OkResponse> =>
    call("claimPlayer", { params: { id }, body: { playerId: id } as any }),
  mergePlayers: (body: MergePlayersRequest): Promise<OkResponse> =>
    call("mergePlayers", { body }),
  /** The owner of a claimed profile sets how they play (ADR-0023). */
  updatePlayerStyle: (id: string, body: UpdatePlayerStyleRequest): Promise<PlayerStyle> =>
    call("updatePlayerStyle", { params: { id }, body }),

  // ── Push notifications (P1) ───────────────────────────────────────────
  /** The VAPID public key, or `{ publicKey: null }` when push is not configured. */
  pushKey: (): Promise<PushKeyResponse> => call("pushKey", {}),
  pushSubscribe: (body: PushSubscribeRequest): Promise<OkResponse> =>
    call("pushSubscribe", { body }),
  pushUnsubscribe: (matchId: string): Promise<OkResponse> =>
    call("pushUnsubscribe", { params: { matchId } }),
};

/** The public page for a match, for sharing. Server-rendered and unauthenticated. */
export const publicMatchPath = (shortcode: string): string =>
  split("publicMatchPage")[1].replace(":shortcode", encodeURIComponent(shortcode));

export const publicTournamentPath = (shortcode: string): string =>
  `/t/${encodeURIComponent(shortcode)}`;
